/** Shared hover/focus/blur style handlers over SDL design tokens. */

import { PROPERTY_BORDER_COLOR } from '@federa/const/common-property-values';
import { coerceString } from '@federa/const/string-guards';
import { colors } from '../css-refs.colors.ts';
import type * as React from 'react';

const PROPERTY_BACKGROUND_COLOR = 'backgroundColor';

const APPLY_HOVER_DELTA_ENTER = 'enter';

const APPLY_HOVER_DELTA_LEAVE = 'leave';

// NO `handleModalOverlayKeyDown` / `handleModalBoxKeyDown` PAIR (2026-09-02). The two existed only to service one
// antipattern: an overlay carrying `role="button"` + `tabIndex={0}`, which announces the whole dimmed backdrop as a
// single button, owes a keyboard equivalent of its backdrop click -- so the pair supplied Enter/Space dismiss on the
// overlay and Enter/Space containment on the box. Their sole consumer, `ledger/RefundConfirmModal.tsx`, folded onto the
// SDL `feedback/ConfirmDialog`, which announces the backdrop as `presentation`, puts `role="dialog"` + `aria-modal` on
// a `FocusTrap` and handles Escape itself. Keeping the pair would have left a ready-made helper whose ONLY correct use
// is the shape the fold removed. A modal needing a keyboard-dismissable shell composes `ConfirmDialog` or
// `primitives/ModalShell`, not this.

function applyBorderAndBackground(element: HTMLElement, borderColor: string, backgroundColor: string): void {
	element.style.borderColor = borderColor;
	element.style.backgroundColor = backgroundColor;
}

function applyInputFocusStyles(element: HTMLElement): void {
	applyBorderAndBackground(element, colors.brand.primary, colors.semantic.infoBackground);
}

function resetInputFocusStyles(element: HTMLElement, defaultBorderColor: string, defaultBackgroundColor: string): void {
	applyBorderAndBackground(element, defaultBorderColor, defaultBackgroundColor);
}

/** `EventTarget` is a DOM ingress boundary, so it is narrowed with `instanceof HTMLElement` rather than cast to a shadow `{ style: Record<string,string> }`. A non-element target no-ops instead of throwing on the absent `.style`. */
export function applyFocusFromEvent(target: EventTarget): void {
	if (!(target instanceof HTMLElement)) return;
	applyInputFocusStyles(target);
}

/** Same `instanceof HTMLElement` narrowing as {@link applyFocusFromEvent}. */
export function resetBlurFromEvent(target: EventTarget, defaultBorderColor: string, defaultBackgroundColor: string): void {
	if (!(target instanceof HTMLElement)) return;
	resetInputFocusStyles(target, defaultBorderColor, defaultBackgroundColor);
}

// `handleSelectFocus` / `handleSelectBlur` are gone (DL-A.2). They were a hand-rolled focus ring for a raw `<select>`:
// two handlers writing `style.borderColor` straight onto the DOM node, which is a ring no disabled, invalid or readonly
// state could ever override. Their two consumers now render the SDL `Select`, which wires focus through
// `buildInputFocusHandlers` and paints the ring from the shared interaction tokens.

/** The style axes the delta factories may mutate; the union AND the iteration order both derive from this tuple. */
const HOVER_DELTA_PROPERTIES = [PROPERTY_BACKGROUND_COLOR, PROPERTY_BORDER_COLOR, 'color', 'opacity', 'textDecorationColor'] as const;

type CSSProperty = (typeof HOVER_DELTA_PROPERTIES)[number];
type MouseHandler = (e: React.MouseEvent<HTMLElement>) => void;

/** One property's applied/reverted value pair. `leave: ''` clears the inline override so the element's stylesheet value reapplies. */
interface HoverDeltaPair {
	enter: string;
	leave: string;
}

/** Multi-property style delta: each named property gets its own enter/leave pair, applied and reverted together. */
type HoverDelta = Partial<Record<CSSProperty, HoverDeltaPair>>;

interface HoverDeltaOptions {
	/** Suppression affordance: `false` makes BOTH handlers no-ops (disabled rows, active tabs, mobile mounts). Defaults to true. */
	enabled?: boolean;
}

function applyHoverDelta(element: HTMLElement, delta: HoverDelta, side: keyof HoverDeltaPair): void {
	for (const property of HOVER_DELTA_PROPERTIES) {
		const pair = delta[property];
		if (pair) element.style[property] = pair[side];
	}
}

/**
 * The canonical replacement for inline `e.currentTarget.style` hover mutations: applies every pair's `enter` value on
 * mouseenter and reverts to `leave` on mouseleave, on `e.currentTarget`. Suppression rides `options.enabled` -- a
 * per-render `enabled: !active` reproduces the `if (!active)` guard the inline idiom spelled at each site.
 */
export function createHoverDeltaHandlers(
	delta: HoverDelta,
	options?: HoverDeltaOptions,
): { onMouseEnter: MouseHandler; onMouseLeave: MouseHandler } {
	const enabled = options?.enabled ?? true;
	return {
		onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
			if (enabled) applyHoverDelta(e.currentTarget, delta, APPLY_HOVER_DELTA_ENTER);
		},
		onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
			if (enabled) applyHoverDelta(e.currentTarget, delta, APPLY_HOVER_DELTA_LEAVE);
		},
	};
}

interface HoverStateConfig {
	normalBg: string;
	hoverBg: string;
	pressBg?: string;
}

interface CoreHandlerConfig {
	hoverValue: string;
	defaultValue: string;
	property: CSSProperty;
	condition?: boolean;
}

/** Single-property adapter over the delta core; `condition: true` suppresses (the inverse of `enabled`). */
function createCoreHandlers(config: CoreHandlerConfig): {
	onMouseEnter: MouseHandler;
	onMouseLeave: MouseHandler;
} {
	const delta: HoverDelta = {};
	delta[config.property] = { enter: config.hoverValue, leave: config.defaultValue };
	return createHoverDeltaHandlers(delta, { enabled: !config.condition });
}

export function createHoverHandlers(config: HoverStateConfig) {
	const handlers = createCoreHandlers({
		hoverValue: config.hoverBg,
		defaultValue: config.normalBg,
		property: PROPERTY_BACKGROUND_COLOR,
	});

	return {
		...handlers,
		onMouseDown: config.pressBg
			? (e: React.MouseEvent<HTMLElement>) => {
					e.currentTarget.style.backgroundColor = coerceString(config.pressBg);
				}
			: undefined,
	};
}

interface BackgroundHoverConfig {
	hoverColor: string;
	defaultColor?: string;
	property?: CSSProperty;
	condition?: boolean;
}

/** `condition` suppresses hover when true; defaults are transparent/backgroundColor. */
export function createBackgroundHoverHandlers(
	config: BackgroundHoverConfig | string,
	defaultColor: string = 'transparent',
	property: CSSProperty = PROPERTY_BACKGROUND_COLOR,
) {
	const normalizedConfig: BackgroundHoverConfig = typeof config === 'string' ? { hoverColor: config, defaultColor, property } : config;

	return createCoreHandlers({
		hoverValue: normalizedConfig.hoverColor,
		defaultValue: normalizedConfig.defaultColor ?? 'transparent',
		property: normalizedConfig.property ?? 'backgroundColor',
		condition: normalizedConfig.condition,
	});
}
